/**
 * Single source of truth for the holidays analytics.
 *
 * Every holidays event is routed through `useClevertapForHolidays`, which forwards to CleverTap +
 * NewRelic and auto-injects the page / package context. The business event names from the tracking
 * brief are carried as `action_name` on the two CleverTap events — `Holiday_PageLoad` and
 * `Holiday_UI_Action` — exactly as the flights funnel carries its steps on `Air_*`.
 */

import type { HolidayCollection, HolidayPackage } from '@/src/types/holidays'

export const HOLIDAY_EVENT_NAME = {
    PAGE_LOAD: 'Holiday_PageLoad',
    UI_ACTION: 'Holiday_UI_Action'
} as const

/** `action_name` values — one per step of the holidays funnel. */
export const HOLIDAY_ACTION = {
    /** `/holidays` — the list of campaign collections. */
    INDEX_VIEWED: 'holiday_index_viewed',
    /** A collection landing page, e.g. the UAE National Day packages. */
    CATEGORY_VIEWED: 'holiday_category_viewed',
    /** A package detail page. */
    PACKAGE_VIEWED: 'holiday_package_viewed',
    /** A collection card on the index was clicked. */
    CATEGORY_SELECTED: 'holiday_category_selected',
    /** A destination / package card was clicked. */
    PACKAGE_SELECTED: 'holiday_package_selected',
    /** The phone number or the email address was clicked. */
    CONTACT_CLICKED: 'holiday_contact_clicked',
    /** Gallery, itinerary, FAQ or a nav tab was engaged with. */
    CONTENT_ENGAGED: 'holiday_package_content_engaged',
    /** The enquiry form was opened on top of the page — the docked bar's sheet, or the
     *  timed popup. `placement` says which. */
    ENQUIRY_OPENED: 'holiday_enquiry_opened',
    /** First interaction with the "Book today" form. */
    LEAD_FORM_STARTED: 'holiday_lead_form_started',
    /** Validation stopped the submit. */
    LEAD_FORM_ERROR: 'holiday_lead_form_error',
    /** The enquiry was accepted and a lead created. */
    LEAD_SUBMITTED: 'holiday_lead_submitted',
    /** The enquiry call failed — the drop-off the submitted event cannot show. */
    LEAD_FAILED: 'holiday_lead_failed'
} as const

/** High-level interaction bucket, alongside `action_name`. */
export const HOLIDAY_ACTION_TYPE = {
    PAGE_LOAD: 'page_load',
    NAVIGATION: 'navigation',
    CONTACT: 'contact',
    CONTENT: 'content',
    FORM: 'form'
} as const

export const HOLIDAY_PAGE_TYPE = {
    INDEX: 'holiday_index',
    COLLECTION: 'holiday_collection',
    PACKAGE: 'holiday_package'
} as const

export type HolidayActionName = (typeof HOLIDAY_ACTION)[keyof typeof HOLIDAY_ACTION]
export type HolidayActionType = (typeof HOLIDAY_ACTION_TYPE)[keyof typeof HOLIDAY_ACTION_TYPE]
export type HolidayPageType = (typeof HOLIDAY_PAGE_TYPE)[keyof typeof HOLIDAY_PAGE_TYPE]

export type HolidayEventPayload = Record<string, string | number | boolean>

/** Every `Holiday_UI_Action` names its interaction bucket; the type system enforces it. */
export interface HolidayUIActionPayload extends HolidayEventPayload {
    action_type: HolidayActionType
}

/** Authored copy (FAQ questions, tab labels) is trimmed so payloads stay light. */
const MAX_TEXT_LENGTH = 120

export const truncateHolidayText = (value: string) =>
    value.length > MAX_TEXT_LENGTH ? value.slice(0, MAX_TEXT_LENGTH) : value

const CAMPAIGN_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'gclid'] as const

/**
 * Campaign attribution, read from the current URL only. Holidays does not persist these across
 * navigations, so they are populated on the landing hit and report 'N/A' deeper in the flow.
 */
export const getHolidayCampaignParams = (): HolidayEventPayload => {
    if (typeof window === 'undefined') return {}

    const params = new URLSearchParams(window.location.search)
    return CAMPAIGN_KEYS.reduce<HolidayEventPayload>((acc, key) => {
        acc[key] = params.get(key) || 'N/A'
        return acc
    }, {})
}

export const resolveHolidayPageType = (hasPackage: boolean, hasCollection: boolean): HolidayPageType => {
    if (hasPackage) return HOLIDAY_PAGE_TYPE.PACKAGE
    if (hasCollection) return HOLIDAY_PAGE_TYPE.COLLECTION
    return HOLIDAY_PAGE_TYPE.INDEX
}

export const buildHolidayPackagePayload = (holidayPackage: HolidayPackage): HolidayEventPayload => {
    const { summary } = holidayPackage

    return {
        package_id: holidayPackage.slug,
        package_name: summary.destinationName,
        destination: summary.destinationName,
        collection_slug: holidayPackage.collectionSlug,
        campaign_theme: holidayPackage.collectionSlug,
        duration_days: summary.durationDays,
        departure_date: summary.startDate,
        return_date: summary.endDate,
        airline: summary.airline?.name || 'N/A',
        adult_price: summary.startingPrice.amount,
        // Not `currency`: that key is the user's selected currency site-wide, and overwriting it
        // here would make a holidays event disagree with every other product's.
        price_currency: summary.startingPrice.currency
    }
}

export const buildHolidayCollectionPayload = (collection: HolidayCollection): HolidayEventPayload => {
    const packageCount = collection.sections.reduce(
        (total, section) => total + (section.type === 'packageGrid' ? section.packages.length : 0),
        0
    )

    return {
        campaign_theme: collection.slug,
        collection_slug: collection.slug,
        collection_name: collection.hero.heading,
        package_count: packageCount
    }
}
